// Above the grid: warehouse picker, search, filters (Customer, Routing status), date
// range, columns, the colour key (an ⓘ beside Columns), the status tabs with counts and the
// counters (spec §3.3–3.4).

import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CalendarIcon,
  CheckIcon,
  ChevronsDownIcon,
  ChevronsUpIcon,
  Columns3Icon,
  FilterIcon,
  GripVerticalIcon,
  InfoIcon,
  SearchIcon,
  TriangleAlertIcon,
  WarehouseIcon,
} from "lucide-react";
import {
  DEFAULT_WAREHOUSES,
  HISTORY_DATE_FIELDS,
  HISTORY_PRESETS,
  HISTORY_TABS,
  WORKLIST_DATE_FIELDS,
  WORKLIST_PRESETS,
  WAREHOUSE_GROUPS,
  WORKLIST_TABS,
  dayOfWeek,
  formatShortDay,
  parseDateInput,
  presetRange,
  tabInSource,
} from "@app/shared";
import type { DateRange, Filters, Mode, SourceFilter, TabId, WarehouseGroup } from "@app/shared";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  ToolbarButton,
} from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import type { ColumnOrder } from "./column-drag";
import { useMessage } from "./message";
import { DayInputs } from "./pickers";
import { useDialogDrag } from "./dialog-drag";
import { SourceBadge } from "./source-filter";

const WAREHOUSE_LABELS: Record<WarehouseGroup, string> = {
  PE: "Perris, CA (PE)",
  HT: "Hightech 3PL (HT)",
  SM: "Somerset, NJ (SM)",
};

/**
 * The three warehouses (PE, HT, SM), each with its count of lines; several may be ticked. Nothing
 * ticked shows every warehouse.
 */
export function WarehousePicker({
  value,
  counts,
  onChange,
}: {
  value: WarehouseGroup[];
  counts: Record<WarehouseGroup, number>;
  onChange: (w: WarehouseGroup[]) => void;
}) {
  const label =
    value.length === 0 || value.length === WAREHOUSE_GROUPS.length
      ? "All warehouses"
      : value.length === 1
        ? WAREHOUSE_LABELS[value[0]!]
        : value.join(" + ");
  return (
    <Popover>
      <PopoverTrigger
        render={<ToolbarButton icon={<WarehouseIcon />} label={label} hint="Warehouse (A2000 WH): tick one or more" />}
      />
      <PopoverContent align="start" className="w-64 gap-1 p-2 text-[13px]">
        {WAREHOUSE_GROUPS.map((w) => (
          <label key={w} className="hover:bg-muted flex cursor-pointer items-center gap-2 rounded px-2 py-1">
            <Checkbox
              checked={value.includes(w)}
              onCheckedChange={(c) => onChange(c ? [...value, w] : value.filter((x) => x !== w))}
            />
            <span className="flex-1 truncate">{WAREHOUSE_LABELS[w]}</span>
            <span className="text-muted-foreground text-xs tabular-nums">{counts[w] ?? 0}</span>
          </label>
        ))}
        <div className="flex items-center justify-between border-t pt-1.5">
          <span className="text-muted-foreground text-xs">Nothing ticked = all</span>
          <Button size="sm" variant="ghost" onClick={() => onChange(DEFAULT_WAREHOUSES)}>
            PE + HT
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function SearchBox({
  value,
  onChange,
  placeholder = "Search a PO, SO / PT # or customer",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative w-full sm:w-64">
      <SearchIcon className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
      <Input
        className="h-8 pl-8 text-sm"
        placeholder={placeholder}
        aria-label="Search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** A multi-select filter pill: searchable when long, "Nothing ticked = all". */
export function FilterPill({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  /** `tip`: the option's hover, which the search matches too (a shown name's customers: their names and codes). */
  options: { value: string; label: string; count: number; tip?: string }[];
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  const [search, setSearch] = useState("");
  const shown = options.filter(
    (o) => !search || `${o.label} ${o.tip ?? ""}`.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <Popover onOpenChange={(o) => o && setSearch("")}>
      <PopoverTrigger
        render={
          <ToolbarButton
            icon={<FilterIcon />}
            label={label}
            hint={`Filter by ${label.toLowerCase()}`}
            badge={selected.length ? selected.length : undefined}
            variant={selected.length ? "secondary" : "outline"}
          />
        }
      />
      <PopoverContent align="start" className="w-72 gap-1.5 p-2 text-[13px]">
        {options.length > 8 ? (
          <Input
            className="h-7 text-xs"
            placeholder={`Search ${label.toLowerCase()}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        ) : null}
        <div className="max-h-72 overflow-y-auto">
          {shown.map((o) => (
            <label
              key={o.value}
              title={o.tip}
              className="hover:bg-muted flex cursor-pointer items-center gap-2 rounded px-2 py-1"
            >
              <Checkbox
                checked={selected.includes(o.value)}
                onCheckedChange={(c) => onChange(c ? [...selected, o.value] : selected.filter((x) => x !== o.value))}
              />
              <span className="flex-1 truncate">{o.label}</span>
              <span className="text-muted-foreground text-xs tabular-nums">{o.count}</span>
            </label>
          ))}
          {shown.length === 0 ? <p className="text-muted-foreground px-2 py-1">Nothing matches.</p> : null}
        </div>
        <div className="flex items-center justify-between border-t pt-1.5">
          <span className="text-muted-foreground text-xs">Nothing ticked = all</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => onChange(options.map((o) => o.value))}>
              Select all
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onChange([])}>
              Clear
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const fieldLabel = (mode: Mode, id: string) =>
  (mode === "worklist" ? WORKLIST_DATE_FIELDS : HISTORY_DATE_FIELDS).find((f) => f.id === id)?.label ?? id;

export function rangeText(mode: Mode, r: DateRange): string {
  const presets: readonly { id: string; label: string }[] = mode === "worklist" ? WORKLIST_PRESETS : HISTORY_PRESETS;
  const what =
    presets.find((p) => p.id === r.preset)?.label ??
    (r.from || r.to ? `${r.from ? formatShortDay(r.from) : "…"}–${r.to ? formatShortDay(r.to) : "…"}` : "Any date");
  return `${fieldLabel(mode, r.field)}: ${what}`;
}

export function DateRangePicker({
  mode,
  value,
  today,
  onChange,
}: {
  mode: Mode;
  value: DateRange;
  today: string;
  onChange: (r: DateRange) => void;
}) {
  const message = useMessage();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const fields = mode === "worklist" ? WORKLIST_DATE_FIELDS : HISTORY_DATE_FIELDS;
  const presets: readonly { id: string; label: string }[] = mode === "worklist" ? WORKLIST_PRESETS : HISTORY_PRESETS;
  const apply = async () => {
    if (draft.preset !== "custom") {
      onChange({ ...draft, ...presetRange(draft.preset, today) });
      return setOpen(false);
    }
    const a = parseDateInput(from, "routedOn", today);
    const b = parseDateInput(to, "routedOn", today);
    if (!a.ok || !b.ok || !a.value || !b.value) {
      return message.error("Can't filter by these dates", "Enter both dates like 09/01 and 09/30.");
    }
    if (a.value > b.value)
      return message.error("Can't filter by these dates", "The “from” date is after the “to” date.");
    onChange({ ...draft, from: a.value, to: b.value });
    setOpen(false);
  };
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setDraft(value);
          setFrom(value.preset === "custom" && value.from ? formatShortDay(value.from) : "");
          setTo(value.preset === "custom" && value.to ? formatShortDay(value.to) : "");
        }
      }}
    >
      <PopoverTrigger
        render={<ToolbarButton icon={<CalendarIcon />} label={rangeText(mode, value)} hint="Filter by date" />}
      />
      <PopoverContent align="start" className="w-80 gap-3 p-3 text-[13px]">
        <p className="font-semibold">Filter by date</p>
        <div className="flex flex-wrap gap-1.5">
          {fields.map((f) => (
            <Chip key={f.id} active={draft.field === f.id} onClick={() => setDraft({ ...draft, field: f.id })}>
              {f.label}
            </Chip>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {[...presets, { id: "custom", label: "Custom" }].map((p) => (
            <Chip key={p.id} active={draft.preset === p.id} onClick={() => setDraft({ ...draft, preset: p.id })}>
              {p.label}
            </Chip>
          ))}
        </div>
        {draft.preset === "custom" ? (
          <DayInputs from={from} to={to} today={today} onFrom={setFrom} onTo={setTo} />
        ) : null}
        <Button size="sm" onClick={() => void apply()}>
          Apply
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-2.5 py-1 text-xs",
        active ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}

/** A column as the Columns menu lists it: any table's. `kind` colours its dot as the header bar. */
export interface MenuColumn {
  id: string;
  header: string;
  sticky?: boolean;
  kind?: string;
}

const KIND_DOT: Record<string, string> = {
  system: "bg-slate-400",
  calc: "bg-blue-400",
  edit: "bg-emerald-400",
  pickup: "bg-teal-500",
};

/**
 * Show or hide a table's columns, and put them in order: a row dragged (it follows the pointer, the
 * others slide aside, a line shows where it lands; the list scrolls near its edges — `column-drag.tsx`),
 * or moved with its buttons — up, down, to the
 * top, to the bottom. Frozen columns ("Always shown") stay at the top, apart, and never move. Reset
 * puts the default columns and order back.
 */
export function ColumnsMenu({
  columns,
  order,
  hidden,
  onChange,
  onReset,
  rowColors,
  onRowColors,
  pinnable = [],
  unpinned = [],
  onPin,
}: {
  /** In this person's order (`orderColumns`). */
  columns: readonly MenuColumn[];
  order: ColumnOrder;
  hidden: string[];
  onChange: (hidden: string[]) => void;
  onReset: () => void;
  /** The Worklist and History only. */
  rowColors?: boolean;
  onRowColors?: (on: boolean) => void;
  /** Columns pinned by default a person may unpin (`PINNABLE`): a "Pinned" tick on their row. */
  pinnable?: readonly string[];
  unpinned?: readonly string[];
  onPin?: (id: string, pinned: boolean) => void;
}) {
  const [search, setSearch] = useState("");
  const shown = columns.filter((c) => !hidden.includes(c.id)).length;
  const movable = columns.filter((c) => !c.sticky).map((c) => c.id);
  const matches = (c: MenuColumn) => !search || c.header.toLowerCase().includes(search.toLowerCase());
  const frozen = columns.filter((c) => c.sticky && matches(c));
  const rest = columns.filter((c) => !c.sticky && matches(c));
  const row = (c: MenuColumn) => {
    const at = movable.indexOf(c.id);
    const button = (label: string, icon: ReactNode, disabled: boolean, act: () => void) => (
      <button
        type="button"
        aria-label={`${label}: ${c.header}`}
        title={label}
        className="hover:bg-background text-muted-foreground hover:text-foreground rounded p-0.5 disabled:opacity-30"
        disabled={disabled}
        onClick={(e) => {
          // A button in the row: not the row's checkbox.
          e.preventDefault();
          act();
        }}
      >
        {icon}
      </button>
    );
    return (
      <label
        key={c.id}
        className={cn(
          "hover:bg-muted flex min-h-[32px] cursor-pointer items-center gap-2 rounded px-1.5",
          c.sticky && "cursor-default",
        )}
        {...(c.sticky ? {} : order.row(c.id, movable))}
      >
        {c.sticky ? (
          <span className="size-3.5 shrink-0" />
        ) : (
          // `touch-none`: on a touch screen the grip drags the row; elsewhere the finger scrolls the list.
          <GripVerticalIcon className="text-muted-foreground size-3.5 shrink-0 cursor-grab touch-none" aria-hidden />
        )}
        <Checkbox
          disabled={c.sticky}
          checked={!hidden.includes(c.id)}
          onCheckedChange={(on) => onChange(on ? hidden.filter((h) => h !== c.id) : [...hidden, c.id])}
        />
        {c.kind ? <span className={cn("size-2 shrink-0 rounded-sm", KIND_DOT[c.kind] ?? "bg-slate-300")} /> : null}
        <span className="flex-1 truncate">{c.header}</span>
        {onPin && pinnable.includes(c.id) ? (
          <button
            type="button"
            role="checkbox"
            aria-checked={!unpinned.includes(c.id)}
            title={
              unpinned.includes(c.id)
                ? "Pin it on the left again"
                : "Unpin: it scrolls with the other columns, leaving more room for the rest"
            }
            className="hover:bg-background flex shrink-0 items-center gap-1 rounded px-1 text-xs"
            onClick={(e) => {
              // A button in the row: not the row's checkbox.
              e.preventDefault();
              onPin(c.id, unpinned.includes(c.id));
            }}
            data-testid={`pin-${c.id}`}
          >
            <span
              className={cn(
                "flex size-3.5 items-center justify-center rounded-[4px] border",
                !unpinned.includes(c.id) && "bg-primary border-primary text-primary-foreground",
              )}
              aria-hidden
            >
              {unpinned.includes(c.id) ? null : <CheckIcon className="size-3" />}
            </span>
            Pinned
          </button>
        ) : null}
        {c.sticky ? null : (
          <span className="flex shrink-0">
            {button("Move to top", <ChevronsUpIcon className="size-3.5" />, at === 0, () =>
              order.toEdge(c.id, movable, "top"),
            )}
            {button("Move up", <ArrowUpIcon className="size-3.5" />, at === 0, () => order.step(c.id, movable, -1))}
            {button("Move down", <ArrowDownIcon className="size-3.5" />, at === movable.length - 1, () =>
              order.step(c.id, movable, 1),
            )}
            {button("Move to bottom", <ChevronsDownIcon className="size-3.5" />, at === movable.length - 1, () =>
              order.toEdge(c.id, movable, "bottom"),
            )}
          </span>
        )}
      </label>
    );
  };
  return (
    <Popover onOpenChange={(o) => o && setSearch("")}>
      <PopoverTrigger
        render={
          <ToolbarButton
            icon={<Columns3Icon />}
            label="Columns"
            hint="Show, hide or reorder columns"
            badge={`${shown}/${columns.length}`}
          />
        }
      />
      <PopoverContent align="end" className="w-80 gap-1.5 p-2 text-[13px]">
        <Input
          className="h-7 text-xs"
          placeholder="Search columns"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div ref={order.listRef} className="relative max-h-[min(70vh,44rem)] overflow-y-auto">
          {frozen.length ? (
            <div className="mb-1 border-b pb-1">
              <p className="text-muted-foreground px-1.5 pt-0.5 text-[11px] font-medium tracking-wide uppercase">
                Always shown · fixed
              </p>
              {frozen.map(row)}
            </div>
          ) : null}
          {rest.map(row)}
        </div>
        {onRowColors ? (
          <div className="flex items-center justify-between border-t pt-1.5">
            <label className="flex items-center gap-2 text-xs">
              <Switch size="sm" checked={!!rowColors} onCheckedChange={onRowColors} />
              Row colors as in the workbook
            </label>
          </div>
        ) : null}
        <div className="flex justify-end gap-1 border-t pt-1.5">
          <Button size="sm" variant="ghost" onClick={() => onChange([])}>
            Show all
          </Button>
          <Button size="sm" variant="ghost" onClick={onReset}>
            Reset
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** A Worklist card's left edge: its tab's colour (the dot's). */
const TAB_ACCENT: Partial<Record<TabId, string>> = {
  all: "border-l-foreground",
  review: "border-l-primary",
  overdue: "border-l-red-600",
  now: "border-l-orange-600",
  open: "border-l-amber-500",
  notyet: "border-l-slate-400",
  routed: "border-l-emerald-600",
  hold: "border-l-slate-500",
  norule: "border-l-slate-300",
  gone: "border-l-slate-700",
  dgone: "border-l-slate-700",
  nowms: "border-l-stone-400",
  mixed: "border-l-violet-600",
  twovalues: "border-l-amber-500",
  a2000: "border-l-teal-600",
  dshipped: "border-l-teal-600",
};

export function StatusTabs({
  mode,
  tab,
  counts,
  source = "all",
  onChange,
  noRuleLink,
}: {
  mode: Mode;
  tab: TabId;
  counts: Record<string, number>;
  /** The tabs of one source only (No WMS, Gone from Deposco…) show under All or that source. */
  source?: SourceFilter;
  onChange: (t: TabId) => void;
  /** A manager's: No routing rule's lines fixed in Admin → Routing rules, filtered on No rule. */
  noRuleLink?: () => void;
}) {
  const button = (t: { id: TabId; label: string; dot: string | null }) => (
    <button
      key={t.id}
      role="tab"
      type="button"
      aria-selected={tab === t.id}
      onClick={() => onChange(t.id)}
      className={cn(
        "flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm whitespace-nowrap",
        tab === t.id ? "bg-foreground text-background" : "hover:bg-muted text-muted-foreground",
      )}
    >
      {t.dot ? <span className={cn("size-2 rounded-full", t.dot)} /> : null}
      {t.label}
      <span className={cn("rounded px-1 text-xs tabular-nums", tab === t.id ? "bg-background/20" : "bg-muted")}>
        {counts[t.id] ?? 0}
      </span>
    </button>
  );
  if (mode === "history")
    return (
      <div role="tablist" aria-label="Status" className="flex flex-wrap gap-1">
        {HISTORY_TABS.map(button)}
      </div>
    );
  // A card per tab, in groups that wrap, never scroll: when to route, then what state lines are in.
  // A tab of the other source hides, unless it is the one shown.
  const of = (ids: readonly TabId[]) =>
    WORKLIST_TABS.filter((t) => ids.includes(t.id) && (tabInSource(t.id, source) || t.id === tab));
  const card = (t: (typeof WORKLIST_TABS)[number]) => {
    const on = tab === t.id;
    const n = counts[t.id] ?? 0;
    return (
      <button
        key={t.id}
        type="button"
        aria-pressed={on}
        // The selected card again: back to All open.
        onClick={() => onChange(on && t.id !== "all" ? "all" : t.id)}
        data-testid={`routing-tab-${t.id}`}
        className={cn(
          "focus-visible:ring-ring flex h-16 w-32 shrink-0 flex-col justify-between rounded-md border border-l-4 px-2.5 py-1.5 text-left outline-none focus-visible:ring-2",
          // Nothing in it: greyed, edge too, still a filter to pick.
          !on && n === 0 ? "border-l-border" : (TAB_ACCENT[t.id] ?? "border-l-border"),
          on
            ? "bg-foreground text-background"
            : n === 0
              ? "bg-card text-muted-foreground opacity-60 hover:opacity-100"
              : "bg-card hover:bg-muted",
        )}
      >
        <span className="flex min-w-0 items-start gap-1.5 text-[11px] leading-[13px] font-medium">
          {t.dot ? <span className={cn("mt-[3px] size-2 shrink-0 rounded-full", t.dot)} /> : null}
          <span className="line-clamp-2">{t.label}</span>
        </span>
        <span
          className={cn(
            "text-xl leading-none font-semibold tabular-nums",
            t.id === "overdue" && !on && n > 0 && "text-red-600",
          )}
        >
          {n.toLocaleString("en-US")}
        </span>
      </button>
    );
  };
  // Each group its own bordered panel, a pale colour of its own: the four read apart at a glance.
  const group = (heading: string, ids: readonly TabId[], panel: string, link?: ReactNode) => {
    const cards = of(ids);
    return cards.length ? (
      <div
        key={heading}
        role="group"
        aria-label={heading}
        className={cn("flex flex-col gap-1 rounded-lg border p-2", panel)}
      >
        <span className="text-muted-foreground flex items-center gap-2 text-[10px] leading-3 font-semibold tracking-wider uppercase">
          {heading}
          {link}
        </span>
        <div className="flex flex-wrap gap-1.5">{cards.map(card)}</div>
      </div>
    ) : null;
  };
  return (
    <div aria-label="Status" className="flex flex-col gap-2" data-testid="routing-status-cards">
      <div className="flex flex-wrap gap-x-3 gap-y-2">
        {group("When to route", ["all", "review", "overdue", "now", "open", "notyet"], "border-sky-200 bg-sky-50/70")}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-2">
        {group("Routing status", ["routed", "hold"], "border-emerald-200 bg-emerald-50/70")}
        {group(
          "Data problems",
          ["norule", "nowms", "mixed", "twovalues"],
          "border-amber-200 bg-amber-50/70",
          noRuleLink && (counts.norule ?? 0) > 0 ? (
            <button
              type="button"
              className="text-primary ml-auto font-medium tracking-normal normal-case hover:underline"
              title="Admin → Routing rules, the customers with no rule first"
              onClick={noRuleLink}
              data-testid="no-rule-link"
            >
              Set routing rules →
            </button>
          ) : null,
        )}
        {group("Gone or shipped", ["gone", "dgone", "a2000", "dshipped"], "border-violet-200 bg-violet-50/70")}
      </div>
    </div>
  );
}

/** The legend's rows: each chip or swatch as the grid shows it, and what it means in one line. */
const LEGEND: { section: string; worklistOnly?: boolean; rows: [ReactNode, string][] }[] = [
  {
    section: "Review",
    worklistOnly: true,
    rows: [
      [
        <span className="rounded bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white">New</span>,
        "New WMS — click to approve",
      ],
      [
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800 ring-1 ring-amber-400">
          Updated
        </span>,
        "A2000 or Deposco changed a pick ticket's First ship, Cancel or Received date, QTY, PO or pick tickets — or someone changed READY DATE or SCHEDULED SHIP DATE — since approval — click to approve",
      ],
      [
        <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-800 ring-1 ring-violet-400">
          Split
        </span>,
        "Came from other WMS lines — click to approve",
      ],
      [
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">2 values</span>,
        "Choose one",
      ],
      [
        <span className="cell-changed inline-flex items-center gap-1 rounded px-1.5">
          <TriangleAlertIcon className="size-3 text-amber-500" />
          <s>old</s> → new
        </span>,
        "On the cell that changed; hover: was, now, since when",
      ],
      [
        <span className="rounded bg-violet-600 px-1.5 py-0.5 text-[10px] font-bold text-white">Mixed</span>,
        "More than one customer or date in one WMS (allowed)",
      ],
    ],
  },
  {
    section: "Cells",
    rows: [
      [<span className="cell-locked inline-block h-4 w-8 rounded-sm border" />, "Grey = from the systems (locked)"],
      [<span className="cell-calc inline-block h-4 w-8 rounded-sm border" />, "Blue = calculated"],
      [<span className="inline-block h-4 w-8 rounded-sm border bg-white" />, "White = you fill it in"],
    ],
  },
  {
    section: "Row color",
    rows: [
      [<span className="row-ready rounded px-1">Ready</span>, "Production Notes say READY"],
      [<span className="row-back rounded px-1">Back order</span>, "BACK ORDER — waiting on stock"],
      [<span className="row-cancel rounded px-1">Cancelled</span>, "Cancelled, or gone from A2000"],
    ],
  },
];

/**
 * The legend behind an ⓘ in the toolbar (Worklist and History): the chips and colors in a popover,
 * and from it "What do the colors mean?", each one at length.
 */
export function ColorKey({ mode }: { mode: Mode }) {
  const drag = useDialogDrag();
  const [legend, setLegend] = useState(false);
  const [open, setOpen] = useState(false);
  return (
    <>
      <Popover open={legend} onOpenChange={setLegend}>
        <PopoverTrigger
          render={
            <ToolbarButton
              icon={<InfoIcon />}
              label="What the colors and statuses mean"
              hint="Legend"
              iconOnly
              data-testid="routing-legend"
            />
          }
        />
        <PopoverContent align="end" className="w-[360px] max-w-[calc(100vw-2rem)] gap-2 p-3 text-xs">
          <p className="text-sm font-medium">What the statuses and colors mean</p>
          {LEGEND.filter((s) => mode === "worklist" || !s.worklistOnly).map((s) => (
            <section key={s.section} className="space-y-1">
              <h3 className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">{s.section}</h3>
              <dl className="grid grid-cols-[6.5rem_1fr] items-center gap-x-3 gap-y-1.5">
                {s.rows.map(([chip, text]) => (
                  <Fragment key={text}>
                    <dt className="flex">{chip}</dt>
                    <dd>{text}</dd>
                  </Fragment>
                ))}
              </dl>
            </section>
          ))}
          <button
            type="button"
            className="text-primary self-start py-1 hover:underline"
            onClick={() => {
              setLegend(false);
              drag.reset();
              setOpen(true);
            }}
          >
            What do the colors mean?
          </button>
        </PopoverContent>
      </Popover>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent ref={drag.ref} style={drag.style} initialFocus={drag.ref} className="sm:max-w-lg">
          <DialogHeader {...drag.handle}>
            <DialogTitle>What do the colors mean?</DialogTitle>
          </DialogHeader>
          <table className="text-sm">
            <tbody className="[&_td]:py-1.5 [&_td]:pr-3 [&_td]:align-top">
              <tr>
                <td>
                  <span className="rounded bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white">New</span>
                </td>
                <td>A WMS # just arrived from A2000. One line is one WMS #. Click the chip to approve it.</td>
              </tr>
              <tr>
                <td>
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800 ring-1 ring-amber-400">
                    Updated
                  </span>
                </td>
                <td>
                  Changed after you approved it: in A2000 or Deposco, a pick ticket's First ship, Cancel or Received
                  date (any pick ticket, not only the earliest), the QTY, the PO, or a pick ticket (SO) added or
                  removed; or someone changed READY DATE or SCHEDULED SHIP DATE. Each changed cell is marked. Click to
                  approve the change; a date changed back to what was approved is Approved again.
                </td>
              </tr>
              <tr>
                <td>
                  <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-800 ring-1 ring-violet-400">
                    Split
                  </span>
                </td>
                <td>
                  A2000 moved pick tickets here from other WMS lines. Their entries came along (all but Scheduled Ship
                  Date). Click to approve.
                </td>
              </tr>
              <tr>
                <td>
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                    2 values
                  </span>
                </td>
                <td>The lines it came from disagree on this field. Click it to choose a value, or type a new one.</td>
              </tr>
              <tr>
                <td>
                  <span className="rounded bg-violet-600 px-1.5 py-0.5 text-[10px] font-bold text-white">Mixed</span>
                </td>
                <td>
                  One WMS holds several customers, divisions or dates. Allowed: information only. The row has a violet
                  edge and light violet stripes; the Mixed tab lists them.
                </td>
              </tr>
              <tr>
                <td className="whitespace-nowrap">
                  <SourceBadge source="A2000" /> <SourceBadge source="Deposco" />
                </td>
                <td>
                  Where the line comes from: an A2000 WMS #, or a Deposco customer + PO (+ DC). The Source filter above
                  the grid shows one or both.
                </td>
              </tr>
              <tr>
                <td>
                  <span className="rounded bg-violet-600 px-1.5 py-0.5 text-[10px] font-bold text-white">MIXED</span>
                </td>
                <td>
                  A Deposco line whose SOs have different statuses: it shows the highest. Hover it (or open the line) to
                  see which SO is behind, and fix it in Deposco.
                </td>
              </tr>
              <tr>
                <td>
                  <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-bold text-stone-600 ring-1 ring-stone-300">
                    No WMS
                  </span>
                </td>
                <td>An open pick ticket with no WMS # yet (its own tab). Read-only until A2000 gives it one.</td>
              </tr>
              <tr>
                <td>
                  <span className="cell-changed rounded px-1.5">
                    <s>old</s> → new
                  </span>
                </td>
                <td>Shows the old value crossed out and the new one, until the line is approved.</td>
              </tr>
              <tr>
                <td>
                  <span className="cell-locked inline-block h-4 w-8 rounded-sm border" />
                </td>
                <td>Comes from A2000. It can't be typed over.</td>
              </tr>
              <tr>
                <td>
                  <span className="inline-block h-4 w-8 rounded-sm border bg-white" />
                </td>
                <td>The warehouse fills it in. Click it, type, press Enter.</td>
              </tr>
              <tr>
                <td>
                  <span className="cell-calc inline-block h-4 w-8 rounded-sm border" />
                </td>
                <td>Calculated by the tool (for example the routing window).</td>
              </tr>
              <tr>
                <td>
                  <span className="row-ready inline-block h-4 w-8 rounded-sm border" />
                </td>
                <td>Production Notes say READY.</td>
              </tr>
              <tr>
                <td>
                  <span className="row-back inline-block h-4 w-8 rounded-sm border" />
                </td>
                <td>BACK ORDER — waiting on stock.</td>
              </tr>
              <tr>
                <td>
                  <span className="row-cancel inline-block h-4 w-8 rounded-sm border" />
                </td>
                <td>Cancelled, or gone from A2000.</td>
              </tr>
            </tbody>
          </table>
          <p className="text-muted-foreground text-xs">
            ↑↓←→ move · Enter edit · Esc cancel · Delete clear · Ctrl Z undo
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** "Sat, Oct 18 · 4 to route now or late · 12 to review · 30 older open lines". */
export function Counters({
  today,
  late,
  toReview,
  older,
  onReview,
  onOlder,
  children,
}: {
  today: string;
  late: number;
  toReview: number;
  older: number;
  onReview: () => void;
  onOlder: () => void;
  /** More counters at the end: the Deposco orders waiting for a group. */
  children?: ReactNode;
}) {
  const d = new Date(`${today}T12:00:00`);
  return (
    <p className="text-sm">
      <span className="text-muted-foreground">
        {dayOfWeek(today)}, {d.toLocaleString("en-US", { month: "short" })} {d.getDate()} ·{" "}
      </span>
      <strong className={cn(late > 0 && "text-red-700")}>{late} to route now or late</strong>
      <span className="text-muted-foreground"> · </span>
      <button type="button" className="text-primary py-1 hover:underline" onClick={onReview}>
        {toReview} to review
      </button>
      {older > 0 ? (
        <>
          <span className="text-muted-foreground"> · </span>
          <button type="button" className="text-primary py-1 hover:underline" onClick={onOlder}>
            {older} older open line{older === 1 ? "" : "s"}
          </button>
        </>
      ) : null}
      {children ? (
        <>
          <span className="text-muted-foreground"> · </span>
          {children}
        </>
      ) : null}
    </p>
  );
}

export type { Filters };
